import { Component, OnInit, Output, EventEmitter, ViewChild } from '@angular/core';
import { SelectItem, Dropdown, MultiSelect } from 'primeng/primeng';
import { AbstractControl, FormGroup, FormBuilder } from '@angular/forms';
import { StrategyMapDownloadService, DownloadType, DownloadFormConfiguration } from '../strategy-map-download.service';
import { InitiativeAndPriority } from '../initiative-and-priority';
import { IStrategyMapDownloadHeadings } from '../../services/IStrategyMapDownloadHeadings';
import { Subscription } from 'rxjs';

@Component({
  selector: 'app-strategy-map-form',
  templateUrl: './strategy-map-form.component.html',
  styleUrls: ['./strategy-map-form.component.scss']
})
export class StrategyMapFormComponent implements OnInit {
  @Output() close = new EventEmitter<void>();
  @ViewChild(Dropdown) priorityDropdown: Dropdown;
  @ViewChild(MultiSelect) multiSelect: MultiSelect;
  strategyMapDownloadForm: FormGroup;
  resetFormSubscription: Subscription;
  private readonly DEFAULT_PRIORITIES_PER_PAGE = 10;

  constructor(private strategyMapDownloadService: StrategyMapDownloadService,
              private formBuilder: FormBuilder) { }

  pageSizeOptions: SelectItem[]  = [
    {label: 'Show All Strategic Themes On One Page', value: '10'},
    {label: 'Show Up To 5 Strategic Themes On One Page', value: '5'},
    {label: 'Show Up To 3 Strategic Themes On One Page', value: '3'},
    {label: 'Show 1 Strategic Theme On One Page', value: '1'}
  ];

  ngOnInit() {
    this.resetFormSubscription = this.strategyMapDownloadService.resetForm$
      .subscribe(
        (formConfig: DownloadFormConfiguration) => {
          this.resetForm(formConfig);
        });
  }

  get allCheckbox(): AbstractControl {
    return this.strategyFormControls.allCheckbox;
  }

  get strategyMapCheckbox(): AbstractControl {
    return this.strategyFormControls.strategyMapCheckbox;
  }

  get priorityCount(): AbstractControl {
    return this.strategyFormControls.priorityCount;
  }

  get annualPlansCheckbox(): AbstractControl {
    return this.strategyFormControls.annualPlansCheckbox;
  }

  get annualStartDate(): AbstractControl {
    return this.strategyFormControls.annualStartDate;
  }

  get annualEndDate(): AbstractControl {
    return this.strategyFormControls.annualEndDate;
  }

  get implCheckbox(): AbstractControl {
    return this.strategyFormControls.implCheckbox;
  }

  get implStartDate(): AbstractControl {
    return this.strategyFormControls.implStartDate;
  }

  get implEndDate(): AbstractControl {
    return this.strategyFormControls.implEndDate;
  }

  get theoryOfActionsCheckbox(): AbstractControl {
    return this.strategyFormControls.theoryOfActionsCheckbox;
  }

  get theoryOfActionsMultiSelect(): AbstractControl {
    return this.strategyFormControls.theoryOfActionsMultiSelect;
  }

  get secondaryCheckboxes(): AbstractControl[] {
    return [this.strategyMapCheckbox, this.annualPlansCheckbox, this.implCheckbox, this.theoryOfActionsCheckbox];
  }

  get secondaryFormControls(): AbstractControl[] {
    return [this.priorityCount, this.annualStartDate, this.annualEndDate,
       this.implStartDate, this.implEndDate, this.theoryOfActionsMultiSelect];
  }

  get downloadType(): DownloadType {
    return this.strategyMapDownloadService.downloadType;
  }

  get downloadTypeString(): string {
    if (this.downloadType === DownloadType.download) {
      return 'Download';
    } else {
      return 'Share';
    }
  }

  get initiativeTemplateItems(): SelectItem[] {
    return this.strategyMapDownloadService.initiativeTemplateItems;
  }

  get strategyFormControls(): { [key: string]: AbstractControl } {
    return this.strategyMapDownloadForm.controls;
  }

  noDocTypeSelected(): boolean {
    return this.strategyFormControls.submitButton.touched &&
      (!this.strategyFormControls.pdfDoc.value && !this.strategyFormControls.wordDoc.value);
  }

  noImplStartDate(): boolean {
    return this.strategyFormControls.submitButton.touched &&
      (this.implCheckbox.value && this.implStartDate.value == null);
  }

  implDatesInvalid(): boolean {
    return this.strategyFormControls.submitButton.touched && (
      this.implCheckbox.value && this.implEndDate.value && (this.implEndDate.value < this.implStartDate.value)
    );
  }

  noAnnualPlanStartDate(): boolean {
    return this.strategyFormControls.submitButton.touched &&
    (this.annualPlansCheckbox.value && this.annualStartDate.value == null);
  }

  annualPlanDatesInvalid(): boolean {
    return this.strategyFormControls.submitButton.touched &&
    (this.annualPlansCheckbox.value && this.annualEndDate.value && (this.annualEndDate.value < this.annualStartDate.value));
  }

  noIntiativeSelected(): boolean {
    return this.strategyFormControls.submitButton.touched &&
    (this.theoryOfActionsCheckbox.value && this.theoryOfActionsMultiSelect.value.length === 0);
  }

  noOutputsSelected(): boolean {
    return this.strategyFormControls.submitButton.touched &&
      (!this.allCheckbox.value &&
       !this.annualPlansCheckbox.value &&
       !this.theoryOfActionsCheckbox.value &&
       !this.implCheckbox.value &&
       !this.strategyMapCheckbox.value);
  }

  formIsInvalid(): boolean {
    return (this.noDocTypeSelected() ||
            this.implDatesInvalid() ||
            this.noImplStartDate() ||
            this.annualPlanDatesInvalid() ||
            this.noAnnualPlanStartDate() ||
            this.noIntiativeSelected() ||
            this.noOutputsSelected());
  }

  onSelectAllChange(): void {
    if (this.strategyFormControls.allCheckbox.value) {
      // uncheck and disable all the other checkboxes
      this.secondaryCheckboxes.forEach(chk => {
        chk.setValue(false);
        chk.disable();
      });
      // disable all their associated controls
      this.secondaryFormControls.forEach(ctrl => {
        ctrl.disable();
        ctrl.setValue(null);
      });
    } else {
      // enable all the checkboxes
      this.secondaryCheckboxes.forEach(chk => {
        chk.enable();
      });
    }
  }

  onDropDownChange(event: {originalEvent: MouseEvent, value: Array<any>}): void {
    if (event.value.length === 1) {
      if (event.value[0] === null) { // if the select all option is selected, which has a value of null
        this.multiSelect.toggleAll(event, {checked: true});
      }
    }
  }

  selectAllItemsClick(event: any): void {
    const areAllSelected = this.multiSelect.value.length === this.initiativeTemplateItems.length - 1;
    this.multiSelect.toggleAll(event, {checked: areAllSelected});
  }

  onHideModalClick(): void {
    this.strategyMapDownloadService.clearVisible();
    this.close.emit();
  }

  onClickDownloadButton(): void {
    this.strategyFormControls.submitButton.markAsTouched();
    if (!this.formIsInvalid()) {
      this.strategyMapDownloadService.submitForm(this.strategyMapDownloadHeadings());
    }
  }

  onOutputsCheckboxChange(checked: boolean, controls: string[]) {
    if (checked) {
      controls.forEach(ctrl => {
        this.strategyFormControls[ctrl].enable();
      });
    } else {
      controls.forEach(ctrl => {
        this.strategyFormControls[ctrl].disable();
      });
    }
  }

  resetForm(config: DownloadFormConfiguration): void {
    this.strategyMapDownloadForm = null;

    if (config.isPlanEmpty) {
      this.priorityDropdown.options = this.pageSizeOptions.filter((i: SelectItem) => i.value === '3');
    } else {
      this.priorityDropdown.options = this.pageSizeOptions;
    }

    this.strategyMapDownloadForm = this.formBuilder.group(
      {
        allCheckbox: [{ value: config.allCheckbox, disabled: false}],
        strategyMapCheckbox: [{ value: config.strategyMapCheckbox, disabled: config.allCheckbox }],
        priorityCount: [{ value: null, disabled: !config.strategyMapCheckbox }],
        annualPlansCheckbox: [{ value: config.annualPlansCheckbox, disabled: config.allCheckbox }],
        annualStartDate: [{ value: config.annualPlanStartDate, disabled: !config.annualPlansCheckbox }],
        annualEndDate: [{ value: config.annualPlanEndDate, disabled: !config.annualPlansCheckbox }],
        implCheckbox: [{ value: config.implPlanCheckbox, disabled: config.allCheckbox }],
        implStartDate: [{ value: config.implPlanStartDate, disabled: !config.implPlanCheckbox }],
        implEndDate: [{ value: config.implPlanEndDate, disabled: !config.implPlanCheckbox }],
        theoryOfActionsCheckbox: [{ value: config.theoryOfActionCheckBox, disabled: config.allCheckbox }],
        theoryOfActionsMultiSelect: [{ value: [], disabled: !config.theoryOfActionCheckBox }],
        pdfDoc: { value: true },
        wordDoc: { value: true },
        submitButton: [config.submitButton, []]
      }
    );
  }

  private docTypes(): string[] {
    const docTypes: string[] = [];
    if (this.strategyFormControls.pdfDoc.value) {
      docTypes.push('PDF');
    }
    if (this.strategyFormControls.wordDoc.value) {
      docTypes.push('WORD');
    }
    return docTypes;
  }

  private initiativeIds(): number[] {
    const initiativeIds: number[] = [];
    if (this.theoryOfActionsMultiSelect.value) {
      this.theoryOfActionsMultiSelect.value.forEach((e: InitiativeAndPriority) => {
        initiativeIds.push(e.id);
      });
    }
    return initiativeIds;
  }

  private strategyMapDownloadHeadings(): IStrategyMapDownloadHeadings {
    return {
      annualPlanEndDate: this.annualEndDate.value,
      annualPlanStartDate: this.annualStartDate.value,
      downloadAllDeliverables: this.allCheckbox.value,
      downloadAnnualPlans: this.annualPlansCheckbox.value,
      downloadFormats: this.docTypes(),
      downloadImplementationPlans: this.implCheckbox.value,
      downloadStrategyMap: this.strategyMapCheckbox.value,
      downloadTheoryOfActions: this.theoryOfActionsCheckbox.value,
      implPlanEndDate: this.implEndDate.value,
      implPlanStartDate: this.implStartDate.value,
      initiatives: this.initiativeIds(),
      emailRecipients: [],
      workspaceId: null,
      prioritiesPerPage: this.priorityCount.value ?
        this.priorityCount.value :
        this.DEFAULT_PRIORITIES_PER_PAGE
    };
  }

}
